import Breadcrumbs from '@storeblocks/breadcrumbs';
import { type FC } from 'react';
import { styled } from 'styled-components';

import { useOrganizationCmId } from '@/api/userSettings/hooks/useOrganizationCmId';
import { FundAvailabilityAlert } from '@/components/Alert/FundAvailabilityAlert';
import { AnnouncementWarning } from '@/components/AnnouncementWarning';
import { CenteredPageContent } from '@/components/CenteredPageContent';
import { CardColumn, Row } from '@/components/Grid';
import { InternalLink } from '@/components/InternalLink';
import { MainContent } from '@/components/MainContent';
import { NewsArticles } from '@/components/NewsArticles';
import { NewsLinks } from '@/components/NewsLinks';
import { PageHeading } from '@/components/PageHeading';
import { RenderHtml } from '@/components/RenderHtml';
import { AssetAllocationBannerWidget } from '@/components/Widgets/AssetAllocation/AssetAllocationBanner';
import { FundsSustainabilityBannerWidget } from '@/components/Widgets/FundsSustainability/FundsSustainabilityBanner';
import { useTexts } from '@/hooks/useTexts';

export const NewsPage: FC = () => {
  const organizationCmId = useOrganizationCmId();
  const texts = useTexts();

  const rootPath = organizationCmId ? `/${organizationCmId}` : '/';

  return (
    <CenteredPageContent data-test="news-page">
      <AnnouncementWarning />

      <Breadcrumbs expandAllAriaLabel={texts.breadcrumbs.expandAllAriaLabel}>
        <InternalLink to={rootPath}>{texts.menu.links.home}</InternalLink>
        <InternalLink to={`${rootPath}/news`}>
          {texts.menu.links.news}
        </InternalLink>
      </Breadcrumbs>

      <MainContent>
        <PageHeading
          header={texts.menu.links.news}
          ingress={<RenderHtml html={texts.tile.News.description} />}
        />

        <FundAvailabilityAlert contentType="content" fluid />

        <NewsArticles limit={9} />

        <NewsLinks />

        <Row>
          <StyledCardColumn $span={6}>
            <AssetAllocationBannerWidget />
          </StyledCardColumn>
          <StyledCardColumn $span={6}>
            <FundsSustainabilityBannerWidget />
          </StyledCardColumn>
        </Row>
      </MainContent>
    </CenteredPageContent>
  );
};

const StyledCardColumn = styled(CardColumn)`
  flex-grow: 1;
  display: flex;

  /** The content of the cards are too wide to fit both in one row on screens smaller than 960px. */
  @media (max-width: 960px) {
    grid-column: span 12;
  }
`;
